/* ============================================
   VincularProyectoModal — Vincula un chat a un cliente y a un proyecto

   El caso: el cliente arma un grupo de WhatsApp y nos suma para trabajar. Ese chat
   pertenece a la EMPRESA, no a una persona, y es donde se pide el trabajo real.
   Vinculado, lo que la IA extrae del hilo cae en el Kanban del proyecto en vez de
   quedar suelto en el Backlog.
   ============================================ */
import { useState, useEffect } from 'react'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { puedeVerRuta } from '@/lib/modulos'
import { showToast } from '@/components/layout/Toast'
import { IconBriefcase } from '@/components/icons/Icons'
import type { Cliente, Proyecto, Conversacion } from '@/lib/types'

interface VincularProyectoModalProps {
  conversacion: Conversacion
  onSave: (actualizada: Conversacion) => void
  onClose: () => void
}

export default function VincularProyectoModal({ conversacion, onSave, onClose }: VincularProyectoModalProps) {
  const api = useApi()
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [proyectos, setProyectos] = useState<Proyecto[]>([])
  const [clienteId, setClienteId] = useState<number | ''>(conversacion.cliente_id ?? '')
  const [proyectoId, setProyectoId] = useState<number | ''>(conversacion.proyecto_id ?? '')
  const [cargandoProyectos, setCargandoProyectos] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const { modulos } = useAuth()
  const hayProyectos = puedeVerRuta(modulos, '/proyectos')

  useEffect(() => {
    api.get<Cliente[]>('/api/clientes')
      .then((data) => setClientes([...data].sort((a, b) => a.empresa.localeCompare(b.empresa))))
      .catch(() => showToast('No se pudieron cargar los clientes', 'error'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Los proyectos dependen del cliente: mostrar los de otras empresas solo invita
  // a vincular mal, y el backend lo rechazaría igual.
  useEffect(() => {
    // Sin el módulo de Proyectos no hay nada que pedir: la petición volvería
    // 403 y el toast diría «no se pudieron cargar», que suena a avería cuando
    // en realidad esa persona vincula el chat a la EMPRESA y nada más.
    if (!clienteId || !hayProyectos) {
      setProyectos([])
      return
    }
    setCargandoProyectos(true)
    api.get<Proyecto[]>(`/api/proyectos?cliente_id=${clienteId}`)
      .then(setProyectos)
      .catch(() => showToast('No se pudieron cargar los proyectos', 'error'))
      .finally(() => setCargandoProyectos(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, hayProyectos])

  const cambiarCliente = (valor: number | '') => {
    setClienteId(valor)
    // El proyecto elegido pertenece al cliente anterior: dejarlo sería inconsistente.
    setProyectoId('')
  }

  const guardar = async () => {
    setGuardando(true)
    try {
      const actualizada = await api.put<Conversacion>(
        `/api/chat/conversaciones/${conversacion.id}/vinculacion`,
        { cliente_id: clienteId || null, proyecto_id: proyectoId || null },
      )
      showToast(clienteId ? 'Chat vinculado' : 'Chat desvinculado', 'success')
      onSave(actualizada)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al vincular', 'error')
    } finally {
      setGuardando(false)
    }
  }

  const nombreChat = (conversacion.metadata?.contact_name as string) || conversacion.chat_id

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconBriefcase size={20} /> Vincular a proyecto
          </h2>
          <button className="modal-close" onClick={onClose} type="button">&times;</button>
        </div>

        <div className="modal-body">
          <p className="form-hint">
            Vinculas <strong>{nombreChat}</strong> a una empresa{hayProyectos ? ' y, si quieres, a uno de sus proyectos' : ''}.
            {hayProyectos
              ? ' Las tareas que se identifiquen en este chat van a caer en ese proyecto.'
              : ' Las tareas que se identifiquen en este chat van a quedar asociadas a esa empresa.'}
          </p>

          <div className="form-group">
            <label className="form-label">Empresa</label>
            <select
              className="form-select"
              value={clienteId}
              onChange={(e) => cambiarCliente(e.target.value ? Number(e.target.value) : '')}
              autoFocus
            >
              <option value="">Sin vincular</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>{c.empresa}</option>
              ))}
            </select>
          </div>

          {/* Sin el módulo de Proyectos el bloque entero sobra: no hay nada
              que elegir y el backend ni aceptaría el campo. */}
          {hayProyectos && (
            <div className="form-group">
              <label className="form-label">Proyecto</label>
              <select
                className="form-select"
                value={proyectoId}
                onChange={(e) => setProyectoId(e.target.value ? Number(e.target.value) : '')}
                disabled={!clienteId || cargandoProyectos}
              >
                <option value="">
                  {!clienteId
                    ? 'Elige primero una empresa'
                    : cargandoProyectos
                      ? 'Cargando…'
                      : proyectos.length === 0
                        ? 'Esta empresa no tiene proyectos'
                        : 'Sin proyecto'}
                </option>
                {proyectos.map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} type="button" disabled={guardando}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={guardar} type="button" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  )
}
